// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { hasLengthExactly } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { isSignificant, projectBoxToNDC } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { ConnectorLineGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/ConnectorLineGeometry';
import {
  HighlightableGroup,
  type HighlightableObjectName,
  toHighlightableObjectName,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { LabelBillboard } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/LabelBillboard';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';
import { LeaderLineGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/LeaderLineGeometry';
import {
  computeInitialBillboardPlacement,
  computeMultiAnchorLeaderTipsFromBillboard,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/leaderLineLayout';

export type DimensionLabelOpts = {
  label: string;
  value: number;
  group: THREE.Group;
  /** Optional camera layer for the label. If not given, layer zero will be used, which is visible to all cameras */
  layer?: number;
  /**
   * When false, only the axis label is shown without the measured value.
   * @defaultValue true
   **/
  showValue?: boolean;
  /**
   * NDC distance the text / connector line is pushed from the anchor(s).
   * Increase to move a label further from the geometry; decrease to pull it closer.
   * @defaultValue {@link labelTheming.defaultPreferredDistance}
   */
  ndcLabelOffset?: number;
  /**
   * Whether the label may sit on top of the bend geometry.
   *
   * When false, the push distance has to carry the anchors out of the geometry's projected bounds
   * before `ndcLabelOffset` is added, so the label stands off the geometry. When true that clearance
   * is skipped and the label is offset from the anchor centroid by `ndcLabelOffset` alone — use it
   * where sitting within the bounds reads better than being pushed out to the edge of the view.
   * @defaultValue false
   */
  canPlaceInsideGeometry?: boolean;
  /**
   * Controls which side of the measurement the connector line is placed on
   *
   * - `'outward'`: the connector line is placed on the side away from the geometry
   *   center — the standard appearance for most measurements.
   * - `'inward'`: the connector line is placed on the side towards the geometry center. Use
   *   this when the default placement would crowd a nearby label (e.g. a diagonal measurement
   *   that sits close to a perpendicular one).
   *
   * @defaultValue 'outward'
   */
  directionPreference?: 'inward' | 'outward';
};

/**
 * Clamps a billboard's NDC center, in place, so its edges (center ± half-extents) stay within `ndcExtent`.
 * A billboard wider than the extent is centered instead.
 */
const clampBillboardCenter = (center: THREE.Vector2, ndcHalfExtents: THREE.Vector2, ndcExtent: THREE.Vector2) => {
  const limit = ndcExtent.clone().sub(ndcHalfExtents).max(new THREE.Vector2());
  return center.clamp(limit.clone().negate(), limit);
};

/**
 * Base class for dimension labels in the bend preview.
 *
 * Subclasses pass their geometry as the first constructor argument and implement:
 *  - `getAnchorWorldPoints()` – returns one world-space point per leader line.
 *  - `formatValue()` – formats the numeric dimension value for display.
 *
 * `finishSetup` (layer masking and scene registration) runs automatically at the end of this
 * constructor, so subclasses never need to call it explicitly.
 */
export abstract class DimensionLabel extends HighlightableGroup {
  layer?: number;
  private labelBillboard: LabelBillboard;
  private leaderLines: LeaderLineGeometry[] = [];
  private readonly connectorLine: ConnectorLineGeometry;
  readonly label: string;
  private readonly _parentGroup: THREE.Group;
  geometry: HighlightableGroup;
  /** Controls which side of the measurement the connector line is placed on in orthographic views. */
  protected directionPreference: 'outward' | 'inward';
  protected ndcLabelOffset: number;
  protected canPlaceInsideGeometry: boolean;

  protected constructor(
    makeGeometry: (name: HighlightableObjectName) => HighlightableGroup,
    {
      label,
      value,
      group,
      showValue = true,
      layer,
      ndcLabelOffset = labelTheming.defaultPreferredDistance,
      canPlaceInsideGeometry = false,
      directionPreference = 'outward',
    }: DimensionLabelOpts,
  ) {
    super(toHighlightableObjectName(label));

    this.label = label;
    this._parentGroup = group;
    this.geometry = makeGeometry(this.highlightableObjectName);

    const text = showValue ? { description: label, measurement: this.formatValue(value) } : { description: label };
    this.labelBillboard = new LabelBillboard(this.highlightableObjectName, text, this);

    this.layer = layer;

    this.ndcLabelOffset = ndcLabelOffset;
    this.canPlaceInsideGeometry = canPlaceInsideGeometry;
    this.directionPreference = directionPreference;

    this.connectorLine = new ConnectorLineGeometry(this.highlightableObjectName, layer);
    this.add(this.geometry);
    this.add(this.connectorLine);

    // Apply layer masks to all children, then register in the parent group.
    if (isNotNil(layer)) {
      this.traverse((o) => o.layers.set(layer));
    }
    this._parentGroup.add(this);
  }

  /**
   * Returns the world-space anchor points for this label — one per leader line.
   * Subclasses must override this method.
   * @param camera The camera used to compute the anchor points. The renderer should pass the active camera.
   */
  abstract getAnchorWorldPoints(camera: THREE.Camera): THREE.Vector3[];

  // region Per-frame update — called by updateAll in order each frame across all labels before rendering.

  /**
   * **Phase 1a** — Computes and applies the initial billboard position based on anchor
   * layout and label options (`ndcLabelOffset`, `directionPreference`, `canPlaceInsideGeometry`).
   *
   * Must be called before {@link makeScaleConsistent}.
   */
  placeBillboard(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    geometryBox: THREE.Box3,
    ndcExtent: THREE.Vector2,
  ) {
    if (geometryBox.isEmpty()) return;

    this.updateWorldMatrix(true, false);

    const anchorNDCs = this.getAnchorWorldPoints(camera).map((a) => a.clone().project(camera));
    const bboxNDC = projectBoxToNDC(geometryBox, camera);
    const textNDC = computeInitialBillboardPlacement(
      anchorNDCs,
      bboxNDC,
      this.directionPreference,
      this.ndcLabelOffset,
      this.canPlaceInsideGeometry,
      ndcExtent,
    );
    if (isNil(textNDC)) return;
    this.labelBillboard.position.copy(this.worldToLocal(textNDC.unproject(camera)));
  }

  /**
   * **Phase 1b** — Recomputes the sprite's world-space scale so it always occupies a constant
   * fraction of the viewport, regardless of camera distance or zoom level.
   *
   * Also rescales any geometry markers (arrows / circles) so they stay the same screen size
   * as the user zooms, matching the pixel-space behavior of the `Line2` lines.
   *
   * Must be called after {@link placeBillboard} and before {@link clampToFrustum}.
   */
  makeScaleConsistent(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera) {
    this.labelBillboard.update(camera);
    this.geometry.scaleForCamera?.(camera);
    this.connectorLine.scaleForCamera(camera);
  }

  /**
   * **Phase 2a** — Clamps the billboard's NDC position so its *edges* (center ± half-extents)
   * stay within `ndcExtent`, the canvas less its margins. Accounts for the label's actual on-screen width
   * because {@link makeScaleConsistent} has already finalized the sprite dimensions.
   *
   * Must be called after {@link makeScaleConsistent} and before {@link resolveOverlaps}.
   */
  clampToFrustum(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera, ndcExtent: THREE.Vector2) {
    const { center, ndcHalfExtents } = this.labelBillboard.getNDC(camera);
    const clamped = clampBillboardCenter(center.clone(), ndcHalfExtents, ndcExtent);
    if (clamped.equals(center)) return;
    this.moveBillboardToNDC(clamped, camera);
  }

  /**
   * **Phase 2b** — Iteratively separates overlapping label billboards so they don't draw
   * on top of each other. All work is performed in NDC space, so the algorithm is
   * independent of world scale and camera type.
   *
   * Strategy (per iteration):
   *  1. For every pair of labels check whether their NDC bounding boxes overlap.
   *  2. If they do, push each center outward along the centroid-to-centroid vector by
   *     half the required separation, then clamp to the margins.
   *  3. After all iterations write the resolved NDC positions back to world space.
   *
   * Must be called after all labels have had {@link clampToFrustum} applied, and before
   * any label has had {@link connectLeaderLines} called.
   */
  static resolveOverlaps(
    labels: DimensionLabel[],
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    ndcExtent: THREE.Vector2,
  ) {
    /** Flat NDC clearance added between label edges (0.05 = 5 % of the NDC half-range). */
    const OVERLAP_PADDING = 0.05;
    /** Maximum number of separation passes before giving up. */
    const MAX_ITERATIONS = 10;

    if (labels.length < 2) return;

    // Collect mutable NDC centers and their (fixed) half-extents for this frame.
    const entries = labels.map((label) => {
      const { center, ndcHalfExtents } = label.labelBillboard.getNDC(camera);
      return { label, center, ndcHalfExtents };
    });

    for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
      let anyOverlap = false;

      for (const a of entries) {
        for (const b of entries) {
          if (a === b) continue;
          const delta = b.center.clone().sub(a.center);

          // Axis-aligned overlap (positive = overlapping).
          const overlap = a.ndcHalfExtents
            .clone()
            .add(b.ndcHalfExtents)
            .addScalar(OVERLAP_PADDING)
            .sub(new THREE.Vector2(Math.abs(delta.x), Math.abs(delta.y)));

          if (overlap.x <= 0 || overlap.y <= 0) continue;

          anyOverlap = true;

          // Push along the centroid-to-centroid direction.  If centers coincide,
          // fall back to a horizontal nudge so we always make progress.
          const len = delta.length();
          const pushDirection = isSignificant(len) ? delta.clone().divideScalar(len) : new THREE.Vector2(1, 0);

          // The required push to resolve overlap in each axis projected onto the push
          // direction, capped to Infinity to avoid dividing by 0. We take the min
          // because breaking overlap in either axis alone is sufficient to resolve
          // the AABB collision.
          const pushAlongX = isSignificant(pushDirection.x) ? overlap.x / Math.abs(pushDirection.x) : Infinity;
          const pushAlongY = isSignificant(pushDirection.y) ? overlap.y / Math.abs(pushDirection.y) : Infinity;
          const pushStrength = Math.min(pushAlongX, pushAlongY) * 0.5;
          // Clamp each center so that its edges (center ± ndcHalfExtents) stay within the margins.
          clampBillboardCenter(a.center.addScaledVector(pushDirection, -pushStrength), a.ndcHalfExtents, ndcExtent);
          clampBillboardCenter(b.center.addScaledVector(pushDirection, pushStrength), b.ndcHalfExtents, ndcExtent);
        }
      }

      if (!anyOverlap) break;
    }

    // Write resolved NDC positions back to each label.
    for (const { label, center } of entries) {
      label.moveBillboardToNDC(center, camera);
    }
  }

  /**
   * Helper method to unproject an NDC value into local space.
   */
  private ndcToLocal(camera: THREE.Camera, ndc: THREE.Vector3) {
    return this.worldToLocal(ndc.clone().unproject(camera));
  }

  /**
   * **Phase 3** — Connects leader lines from anchor points to the now-finalized
   * billboard position. Must be called after all Phase 2 operations ({@link clampToFrustum}
   * and {@link resolveOverlaps}) are complete for every label in the scene.
   */
  connectLeaderLines(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera, geometryBox: THREE.Box3) {
    if (geometryBox.isEmpty()) return;

    this.updateWorldMatrix(true, false);

    const anchors = this.getAnchorWorldPoints(camera);

    if (hasLengthExactly(anchors, 1)) {
      const anchorLocal = this.worldToLocal(anchors[0]);
      // Read the billboard group's current (post-resolution) local position.
      // The sprite sits at local origin inside the LabelBillboard group.
      const billboardLocal = this.labelBillboard.position.clone();
      const leaderLine = this.getOrCreateLeaderLine(0);
      leaderLine.updateEndPoints(anchorLocal, billboardLocal);
      leaderLine.unhide();
      this.hideLeaderLinesFrom(1);
      this.connectorLine.hide();
    } else {
      // Use the billboard's actual (post-resolution) NDC position so the connector line
      // always runs through the billboard, regardless of how Phase 2 repositioned it.
      const billboardNDC = this.labelBillboard.getWorldPosition(new THREE.Vector3()).project(camera);
      // Project before the loop — worldToLocal below mutates each anchor in place.
      const anchorNDCs = anchors.map((a) => a.clone().project(camera));
      const leaderTipNDCs = computeMultiAnchorLeaderTipsFromBillboard(anchorNDCs, billboardNDC);
      if (isNil(leaderTipNDCs)) return;

      anchors.forEach((anchor, i) => {
        const tipNDC = leaderTipNDCs[i];
        const tipLocal = this.ndcToLocal(camera, tipNDC ?? new THREE.Vector3());
        const leaderLine = this.getOrCreateLeaderLine(i);
        leaderLine.updateEndPoints(this.worldToLocal(anchor), tipLocal);
        leaderLine.unhide();
      });
      this.hideLeaderLinesFrom(anchors.length);
      const firstTipLocal = this.ndcToLocal(camera, leaderTipNDCs[0] ?? new THREE.Vector3());
      const lastTipLocal = this.ndcToLocal(camera, leaderTipNDCs[leaderTipNDCs.length - 1] ?? new THREE.Vector3());
      this.connectorLine.updateEndPoints(firstTipLocal, lastTipLocal);
      this.connectorLine.unhide();
    }
  }

  // endregion

  /** Call to update all labels in the scene for the current frame. */
  static updateAll(
    labels: DimensionLabel[],
    camera: THREE.OrthographicCamera | THREE.PerspectiveCamera,
    bbox: THREE.Box3,
    /** The NDC half-extents labels stay within, so they keep clear of the canvas margins. */
    ndcExtent: THREE.Vector2,
  ) {
    // Phase 1: Place and scale each label's billboard.
    labels.forEach((label) => {
      label.placeBillboard(camera, bbox, ndcExtent);
      label.makeScaleConsistent(camera);
    });

    // Phase 2: Clamp each billboard to the frustum, then resolve inter-label overlaps.
    labels.forEach((label) => label.clampToFrustum(camera, ndcExtent));
    DimensionLabel.resolveOverlaps(labels, camera, ndcExtent);

    // Phase 3: Connect leader lines to the finalized billboard positions.
    labels.forEach((label) => label.connectLeaderLines(camera, bbox));
  }

  /**
   * Moves the text billboard to a new NDC X/Y position, preserving its current depth.
   * Used by {@link clampToFrustum} and {@link resolveOverlaps}.
   * Leader lines are NOT refreshed here — call {@link connectLeaderLines} (Phase 3) for that.
   */
  moveBillboardToNDC(ndcXY: THREE.Vector2, camera: THREE.Camera) {
    const ndcZ = this.labelBillboard.getWorldPosition(new THREE.Vector3()).project(camera).z;
    const worldPos = new THREE.Vector3(ndcXY.x, ndcXY.y, ndcZ).unproject(camera);
    this.labelBillboard.position.copy(this.worldToLocal(worldPos));
  }

  /** Returns the leader line at index, creating it if it doesn't exist yet. */
  private getOrCreateLeaderLine(index: number): LeaderLineGeometry {
    let line = this.leaderLines[index];
    if (isNil(line)) {
      line = new LeaderLineGeometry(this.highlightableObjectName, this.layer);
      this.add(line);
      this.leaderLines[index] = line;
    }
    return line;
  }

  /** Hides all leader lines at or beyond `fromIndex`. */
  private hideLeaderLinesFrom(fromIndex: number) {
    for (let i = fromIndex; i < this.leaderLines.length; i++) {
      this.leaderLines[i]?.hide();
    }
  }

  /**
   * Subclasses must implement this to format the numeric value as a display string.
   */
  abstract formatValue(value: number): string;

  highlight() {
    super.highlight();
    this.labelBillboard.highlight();
    this.geometry.highlight();
    this.connectorLine.highlight();
    for (const leaderLine of this.leaderLines) {
      leaderLine.highlight();
    }
  }

  unhighlight() {
    super.unhighlight();
    this.labelBillboard.unhighlight();
    this.geometry.unhighlight();
    this.connectorLine.unhighlight();
    for (const leaderLine of this.leaderLines) {
      leaderLine.unhighlight();
    }
  }

  dispose() {
    this.labelBillboard.dispose();
    this.connectorLine.dispose();
    this.geometry.dispose();
    for (const leaderLine of this.leaderLines) {
      leaderLine.dispose();
    }
    this.clear();
    this.removeFromParent();
  }
}
